// TODO: TS: check ts

import { Stack } from "@mui/material";
import { Box } from "@mui/system";
import { useCallback, useRef } from "react";
import { type DropzoneInputProps, type DropzoneRootProps, useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";

import { cn } from "../utils/styling";
import { Button } from "./ButtonNew";
import { Icon } from "./Icon";
import { LinearProgressWithLabel } from "./LinearProgressWithLabel";
import { CenterLoading } from "./Loading";

type DropzoneProps = {
  cancelUpload?: () => void;
  className?: string;
  getInputProps: () => DropzoneInputProps;
  getRootProps: (props?: DropzoneRootProps) => DropzoneRootProps;
  isDragActive?: boolean;
  open?: () => void;
  progress?: number;
  selectedFileName?: string;
  upload?: () => void;
  uploadState?: UploadState;
};

type DropzoneSingleProps = {
  acceptedFormats?: string[];
  className?: string;
  handleFileChange: (file: File) => void;
  hasIcon?: boolean;
};

type UploadState = "FileNotSelected" | "FileSelected" | "UploadingFileMetaData" | "UploadStarted";

export function Dropzone({
  cancelUpload,
  className,
  getInputProps,
  getRootProps,
  isDragActive,
  open,
  progress,
  selectedFileName,
  upload,
  uploadState,
}: DropzoneProps) {
  const { t } = useTranslation();
  const inputProps = getInputProps();

  return (
    <div
      {...getRootProps({
        className: cn(
          "dropzone rounded-ds-0-5x !border-dashed border-ds-subtle",
          isDragActive && "border-ds-accent",
          className
        ),
      })}
    >
      <Stack className="mt-21 flex-col ">
        <input {...inputProps} className={cn("sr-only", inputProps.className)} id="input" />
        {uploadState === "FileNotSelected" && (
          <>
            <Icon iconName="Upload" size="md" />
            <Button className="mt-4 capitalize" onClick={open} size="md" variant="primary">
              {t("general.browse")}
            </Button>
          </>
        )}
        {uploadState === "FileSelected" && (
          <>
            <p className="text-lds-foreground-primary font-ds-body-regular-md">{selectedFileName}</p>
            <Button className="mt-4 capitalize" onClick={upload} size="md" variant="primary">
              {t("general.upload")}
            </Button>
          </>
        )}
        {uploadState === "UploadingFileMetaData" && <CenterLoading label={t("general.checkingFile") as string} />}
        {uploadState === "UploadStarted" && (
          <>
            <Box sx={{ width: "80%" }}>
              <LinearProgressWithLabel value={progress} />
            </Box>
            <p className="sentence-case mt-2 text-lds-foreground-primary font-ds-body-regular-xxs">
              {t("general.uploadingFile")} ... {`${Math.round(progress ?? 0)}%`}
            </p>
            <Button className="mt-4 capitalize" onClick={cancelUpload} size="md" variant="secondary">
              {t("general.cancel")}
            </Button>
          </>
        )}
      </Stack>
    </div>
  );
}

export const DropzoneSingle = ({
  acceptedFormats,
  className,
  handleFileChange,
  hasIcon = false,
}: DropzoneSingleProps) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const onDrop = useCallback(
    (acceptedFile: File[]) => {
      if (!acceptedFile) return;
      handleFileChange(acceptedFile[0]);
    },
    [handleFileChange]
  );

  const { getInputProps, getRootProps, isDragActive } = useDropzone({
    accept: Object.fromEntries((acceptedFormats ?? []).map((format) => [format, [] as string[]])),
    multiple: false,
    noClick: true,
    noKeyboard: true,
    onDrop,
  });

  return (
    <div
      {...getRootProps({
        className: cn(
          "relative rounded-ds-0-5x !border-dashed border-ds-subtle",
          isDragActive && "border-ds-accent",
          hasIcon && "flex flex-col items-center justify-center gap-3",
          className
        ),
      })}
    >
      <input {...getInputProps()} className="hidden" ref={fileInputRef} type="file" />
      {hasIcon && (
        <span aria-hidden="true">
          <Icon iconName="Upload" size="md" />
        </span>
      )}
      <Button
        className={cn(!hasIcon && "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2")}
        onClick={() => fileInputRef.current?.click()}
        size="sm"
        type="button"
        variant="primary"
      >
        {t("general.browse")}
      </Button>
    </div>
  );
};
